import type { ColumnConfig } from '@/components/table/Table';
import {
  RouteDateCell,
  RouteDriverCell,
  RouteExpandToggle,
  RouteFinCell,
  RouteIdCell,
  RouteNameCell,
  RouteOperatorCell,
  RouteOrdersCountCell,
  RouteStatusCell,
  RouteTimeCell,
} from '../components/routesTable/RoutesTableCells';
import RouteStatusMiniBar from '../components/routesTable/RouteStatusMiniBar';
import type { RouteTableRowData } from '../types/routeTable';
import { isRouteReadOnly } from '../utils/routeInteractionRules';

export type { RouteTableRowData } from '../types/routeTable';

const buildStatusCounts = (row: RouteTableRowData) => ({
  completed: row.visitsCompletedCount,
  failed: row.visitsFailedCount,
  pending: row.visitsPendingCount,
  expired: row.visitsExpiredCount,
});

export interface BuildRoutesTableColumnsParams {
  expandedIds: Set<string>;
  onToggleExpand: (routeId: string) => void;
  onEditRoute?: (route: RouteTableRowData) => void;
  commerceTableFilter?: { commerceName: string };
}

export const buildRoutesTableColumns = ({
  expandedIds,
  onToggleExpand,
  onEditRoute,
  commerceTableFilter,
}: BuildRoutesTableColumnsParams): ColumnConfig[] => [
  {
    label: '',
    field: '_expand',
    sortable: false,
    render: (_: unknown, row: RouteTableRowData) => (
      <RouteExpandToggle
        expanded={expandedIds.has(row.id)}
        onToggle={() => onToggleExpand(row.id)}
      />
    ),
  },
  {
    label: 'Nombre',
    field: 'name',
    sortable: true,
    render: (name: string, row: RouteTableRowData) => (
      <RouteNameCell
        name={name}
        // Rutas terminales (cancelada/completada/cerrada) quedan informativas:
        // el nombre deja de ser un atajo al editor de ruta.
        onClick={
          isRouteReadOnly(row.status) ? undefined : () => onEditRoute?.(row)
        }
      />
    ),
  },
  {
    label: 'ID de ruta',
    field: 'id',
    sortable: true,
    render: (_: unknown, row: RouteTableRowData) => (
      <RouteIdCell id={row.id} />
    ),
  },
  ...(commerceTableFilter
    ? ([
        {
          label: `Pedidos de ${
            commerceTableFilter.commerceName.trim() || '…'
          } en la ruta`,
          field: 'commerceOrdersCount',
          sortable: true,
          render: (_: unknown, row: RouteTableRowData) => (
            <RouteOrdersCountCell
              count={
                typeof row.commerceOrdersCount === 'number'
                  ? row.commerceOrdersCount
                  : 0
              }
            />
          ),
        },
        {
          label: 'Total pedidos (ruta)',
          field: 'ordersCount',
          sortable: true,
          render: (value: number) => <RouteOrdersCountCell count={value} />,
        },
      ] satisfies ColumnConfig[])
    : ([
        {
          label: 'Pedidos',
          field: 'ordersCount',
          sortable: true,
          render: (value: number) => <RouteOrdersCountCell count={value} />,
        },
      ] satisfies ColumnConfig[])),
  {
    label: 'Estado de visitas',
    field: '_visitStatus',
    sortable: false,
    render: (_: unknown, row: RouteTableRowData) => {
      const counts = buildStatusCounts(row);
      if (
        counts.completed === null ||
        counts.failed === null ||
        counts.pending === null ||
        counts.expired === null
      ) {
        return <span className='text-lg text-neutral-400'>—</span>;
      }
      return (
        <RouteStatusMiniBar
          completed={counts.completed}
          failed={counts.failed}
          pending={counts.pending}
          expired={counts.expired}
        />
      );
    },
  },
  {
    label: 'Fecha:',
    field: 'fecha',
    sortable: true,
    render: (_: unknown, row: RouteTableRowData) => (
      <RouteDateCell
        scheduled={row.scheduled}
        scheduledCalendarDate={row.scheduledCalendarDate}
      />
    ),
  },
  {
    label: 'Hora de Inicio:',
    field: 'startDate',
    sortable: true,
    render: (value: string) => <RouteTimeCell value={value} />,
  },
  {
    label: 'Hora de Cierre:',
    field: 'endDate',
    sortable: false,
    render: (value: string, row: RouteTableRowData) => <RouteFinCell value={value} scheduled={row.scheduled} />,
  },
  {
    label: 'Operador',
    field: 'operatorLabel',
    sortable: true,
    render: (label: string) => <RouteOperatorCell label={label} />,
  },
  {
    label: 'Conductor',
    field: 'driverLabel',
    sortable: true,
    render: (label: string) => <RouteDriverCell label={label} />,
  },
  {
    label: 'Estado',
    field: 'status',
    sortable: true,
    render: (_: unknown, row: RouteTableRowData) => (
      <RouteStatusCell row={row} />
    ),
  },
];

